<template>
  <v-navigation-drawer
    v-model="drawerModel"
    fixed
    temporary
    right
    width="400"
    class="print-drawer"
  >
    <!-- Header -->
    <v-toolbar flat color="primary" dark>
      <v-toolbar-title class="text-h6">
        <v-icon left>mdi-printer</v-icon>
        Imprimir Reporte de Caja
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="closeDrawer">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <!-- Print Preview -->
    <div class="print-preview-container">
      <div ref="printContent" class="print-content pos-format">
        <!-- Header del Ticket -->
        <div class="ticket-header">
          <div class="company-name">{{ companyName }}</div>
          <div class="company-nit">NIT: {{ companyNit }}</div>
          <div v-if="companyAddress" class="company-address">{{ companyAddress }}</div>
          <div v-if="companyCity" class="company-city">{{ companyCity }}</div>
          <div v-if="companyPhone" class="company-phone">Tel: {{ companyPhone }}</div>
          <div v-if="companyEmail" class="company-email">{{ companyEmail }}</div>
          <div class="separator">{{ "=".repeat(42) }}</div>
          <div class="ticket-title">REPORTE DE CAJA</div>
          <div class="separator">{{ "=".repeat(42) }}</div>
        </div>

        <!-- Información del Reporte -->
        <div class="report-info">
          <div class="info-line">
            <span>Fecha:</span>
            <span>{{ formatDate(reportDate) }}</span>
          </div>
          <div class="info-line">
            <span>Hora:</span>
            <span>{{ formatTime(new Date()) }}</span>
          </div>
          <div class="info-line">
            <span>Usuario:</span>
            <span>{{ userName }}</span>
          </div>
          <div class="info-line">
            <span>Punto de Venta:</span>
            <span>{{ storeName }}</span>
          </div>
          <div class="separator">{{ "=".repeat(42) }}</div>
        </div>

        <!-- Resumen General -->
        <div class="summary-section">
          <div class="section-title">RESUMEN GENERAL</div>
          <div class="summary-line total">
            <span>TOTAL CAJA:</span>
            <span>{{ formatCurrency(totalSales) }}</span>
          </div>
          <div class="summary-line">
            <span>Transacciones:</span>
            <span>{{ totalTransactions }}</span>
          </div>
          <div class="summary-line">
            <span>Ticket Promedio:</span>
            <span>{{ formatCurrency(averageTicket) }}</span>
          </div>
          <div class="separator">{{ "-".repeat(42) }}</div>
        </div>

        <!-- Conceptos de Caja -->
        <div class="payment-section">
          <div class="section-title">CONCEPTOS DE CAJA</div>
          <div
            v-for="payment in paymentMethods"
            :key="payment.label"
            class="payment-line"
          >
            <div class="payment-method">{{ payment.label }}</div>
            <div class="payment-details">
              <span>{{ payment.qty }} trans.</span>
              <span class="payment-value">{{ formatCurrency(payment.value) }}</span>
            </div>
          </div>
          <div class="separator">{{ "-".repeat(42) }}</div>
          <div class="payment-total">
            <span>TOTAL MOVIMIENTOS:</span>
            <span>{{ formatCurrency(totalSales) }}</span>
          </div>
        </div>

        <!-- Tipos de Documento -->
        <div class="document-section">
          <div class="separator">{{ "-".repeat(42) }}</div>
          <div class="section-title">TIPOS DE DOCUMENTO</div>
          <div v-for="docType in documentTypes" :key="docType.label" class="doc-line">
            <div class="doc-type">{{ getDocTypeName(docType.label) }}</div>
            <div class="doc-details">
              <span>{{ docType.qty }} docs.</span>
              <span class="doc-value">{{ formatCurrency(docType.value) }}</span>
            </div>
          </div>
        </div>

        <!-- Formas de Pago por Tipo -->
        <div class="payment-type-section" v-if="paymentMethodsByType.length > 0">
          <div class="separator">{{ "-".repeat(42) }}</div>
          <div class="section-title">FORMAS DE PAGO</div>
          <div v-for="payment in paymentMethodsByType" :key="payment.label" class="payment-type-line">
            <div class="payment-type-name">{{ payment.label }}</div>
            <div class="payment-type-details">
              <span>Ingr: {{ formatCurrency(payment.ingresos) }}</span>
              <span>Egr: {{ formatCurrency(payment.egresos) }}</span>
            </div>
          </div>
        </div>

        <!-- Vendedores -->
        <div class="vendor-section" v-if="vendors.length > 0">
          <div class="separator">{{ "-".repeat(42) }}</div>
          <div class="section-title">VENDEDORES</div>
          <div v-for="vendor in vendors" :key="vendor.label" class="vendor-line">
            <div class="vendor-name">{{ getVendorName(vendor.label) }}</div>
            <div class="vendor-details">
              <span>{{ vendor.qty }} trans.</span>
              <span class="vendor-value">{{ formatCurrency(vendor.value) }}</span>
            </div>
          </div>
        </div>

        <!-- Documentos del Día (Resumen) -->
        <div class="documents-section" v-if="showDocuments">
          <div class="separator">{{ "-".repeat(42) }}</div>
          <div class="section-title">DOCUMENTOS ({{ documentsCount }})</div>
          <div v-for="doc in documentsPreview" :key="doc.id" class="document-line">
            <div class="doc-info">
              <span class="doc-number">{{ doc.doc_number }}</span>
              <span class="doc-type-small">{{ getDocTypeName(doc.doc_type) }}</span>
            </div>
            <div class="doc-value-small">{{ formatCurrency(doc.doc_value) }}</div>
          </div>
          <div v-if="documentsCount > documentsPreview.length" class="more-docs">
            ... y {{ documentsCount - documentsPreview.length }} documentos más
          </div>
        </div>

        <!-- Footer -->
        <div class="ticket-footer">
          <div class="separator">{{ "=".repeat(42) }}</div>
          <div class="footer-text">Sistema ERP - AI-BOS</div>
          <div class="print-time">{{ formatDateTime(new Date()) }}</div>
          <div class="separator">{{ "=".repeat(42) }}</div>
        </div>
      </div>
    </div>

    <!-- Print Options -->
    <v-card flat class="print-options">
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-switch
              v-model="showDocuments"
              label="Incluir lista de documentos"
              dense
              hide-details
            ></v-switch>
          </v-col>
          <v-col cols="12">
            <v-slider
              v-model="documentLimit"
              label="Documentos a mostrar"
              :disabled="!showDocuments"
              min="5"
              max="50"
              step="5"
              thumb-label
              hide-details
            ></v-slider>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Action Buttons -->
    <v-card flat>
      <v-card-actions class="pa-4">
        <v-btn block large color="primary" @click="printReport" :loading="printing">
          <v-icon left>mdi-printer</v-icon>
          Imprimir Reporte
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import { getDocumentTypeName } from "@/utils/documentTypes.js";

export default {
  name: "CashReportPrinter",

  props: {
    drawer: {
      type: Boolean,
      default: false,
    },
    reportData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      printing: false,
      showDocuments: true,
      documentLimit: 20,
    };
  },

  computed: {
    drawerModel: {
      get() {
        return this.drawer;
      },
      set(value) {
        this.$emit("update:drawer", value);
      },
    },

    companyName() {
      try {
        return window.store?.store_name || window.store?.PartyName || "Empresa";
      } catch (e) {
        return "Empresa";
      }
    },

    companyNit() {
      try {
        return (
          window.store?.store_business_id ||
          window.store?.PartyIdentification ||
          "Sin NIT"
        );
      } catch (e) {
        return "Sin NIT";
      }
    },

    companyAddress() {
      try {
        return window.store?.store_address || window.store?.AddressLine || "";
      } catch (e) {
        return "";
      }
    },

    companyCity() {
      try {
        return window.store?.store_city || window.store?.CityName || "";
      } catch (e) {
        return "";
      }
    },

    companyPhone() {
      try {
        return window.store?.store_phone || window.store?.client_phone || "";
      } catch (e) {
        return "";
      }
    },

    companyEmail() {
      try {
        return window.store?.store_email || window.store?.client_email || "";
      } catch (e) {
        return "";
      }
    },

    userName() {
      try {
        return window.profile?.user_email || "Usuario";
      } catch (e) {
        return "Usuario";
      }
    },

    storeName() {
      try {
        return window.store?.store_name || "Punto de Venta";
      } catch (e) {
        return "Punto de Venta";
      }
    },

    reportDate() {
      return this.reportData.date || new Date().toISOString().split("T")[0];
    },

    totalSales() {
      return this.reportData.totalSales || 0;
    },

    totalTransactions() {
      return this.reportData.totalTransactions || 0;
    },

    averageTicket() {
      return this.reportData.averageTicket || 0;
    },

    paymentMethods() {
      return this.reportData.paymentMethods || [];
    },

    documentTypes() {
      return this.reportData.documentTypes || [];
    },

    paymentMethodsByType() {
      return this.reportData.paymentMethodsByType || [];
    },

    vendors() {
      return this.reportData.vendors || [];
    },

    documentsCount() {
      return this.reportData.documents?.length || 0;
    },

    documentsPreview() {
      if (!this.showDocuments) return [];
      return (this.reportData.documents || []).slice(0, this.documentLimit);
    },
  },

  methods: {
    closeDrawer() {
      this.$emit("update:drawer", false);
    },

    getDocTypeName(docType) {
      return getDocumentTypeName(docType);
    },

    getVendorName(email) {
      if (!email || !email.includes("@")) return email;
      return email.split("@")[0];
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-CO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    formatTime(date) {
      return date.toLocaleTimeString("es-CO", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatDateTime(date) {
      return date.toLocaleString("es-CO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatCurrency(value) {
      if (!value && value !== 0) return "$0";
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },

    async printReport() {
      this.printing = true;

      try {
        // Create print window
        const printWindow = window.open("", "_blank");
        const printContent = this.$refs.printContent.innerHTML;

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Reporte de Caja - ${this.formatDate(this.reportDate)}</title>
            <style>
              ${this.getPrintStyles()}
            </style>
          </head>
          <body>
            <div class="pos-format">
              ${printContent}
            </div>
          </body>
          </html>
        `);

        printWindow.document.close();

        // Wait for content to load, then print
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
          this.printing = false;
          this.$emit("printed");
        }, 500);
      } catch (error) {
        console.error("Error al imprimir:", error);
        this.printing = false;
        this.$emit("print-error", error);
      }
    },

    getPrintStyles() {
      return `
        @media print {
          @page {
            size: 80mm auto;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
          }
        }
        
        .pos-format {
          width: 80mm;
          font-family: 'Courier New', monospace;
          font-size: 11px;
          line-height: 1.2;
          color: #000;
          background: #fff;
          padding: 2mm;
          margin: 0 auto;
          font-weight: bold; /* TODO EL TEXTO EN BOLD */
        }
        
        .ticket-header {
          text-align: center;
          margin-bottom: 8px;
        }
        
        .company-name {
          font-weight: bold;
          font-size: 14px;
          margin-bottom: 2px;
          text-align: center;
        }
        
        .company-nit {
          font-size: 11px;
          margin-bottom: 2px;
          text-align: center;
          font-weight: bold;
        }
        
        .company-address {
          font-size: 10px;
          margin-bottom: 1px;
          text-align: center;
          font-weight: bold;
        }
        
        .company-city {
          font-size: 10px;
          margin-bottom: 1px;
          text-align: center;
          font-weight: bold;
        }
        
        .company-phone {
          font-size: 10px;
          margin-bottom: 1px;
          text-align: center;
          font-weight: bold;
        }
        
        .company-email {
          font-size: 10px;
          margin-bottom: 2px;
          text-align: center;
          font-weight: bold;
        }
        
        .ticket-title {
          font-weight: bold;
          font-size: 12px;
          margin-bottom: 4px;
          text-align: center;
        }
        
        .separator {
          text-align: center;
          margin: 4px 0;
          font-weight: bold;
        }
        
        .report-info {
          margin-bottom: 8px;
        }
        
        .info-line,
        .summary-line,
        .payment-line,
        .doc-line,
        .vendor-line {
          display: flex;
          justify-content: space-between;
          margin-bottom: 2px;
          font-weight: bold;
        }
        
        .section-title {
          font-weight: bold;
          text-align: center;
          margin-bottom: 4px;
        }
        
        .total {
          font-weight: bold;
          font-size: 12px;
        }
        
        .payment-method,
        .doc-type,
        .vendor-name {
          font-weight: bold;
        }
        
        .payment-details,
        .doc-details,
        .vendor-details {
          display: flex;
          flex-direction: column;
          text-align: right;
          font-size: 10px;
          font-weight: bold;
        }
        
        .payment-total {
          font-weight: bold;
        }
        
        .document-line {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          margin-bottom: 1px;
          font-weight: bold;
        }
        
        .doc-info {
          display: flex;
          flex-direction: column;
          font-weight: bold;
        }
        
        .doc-type-small {
          font-size: 9px;
          color: #666;
          font-weight: bold;
        }
        
        .payment-type-line {
          display: flex;
          justify-content: space-between;
          margin-bottom: 2px;
          font-weight: bold;
        }
        
        .payment-type-name {
          font-weight: bold;
        }
        
        .payment-type-details {
          display: flex;
          flex-direction: column;
          text-align: right;
          font-size: 10px;
          font-weight: bold;
        }
        
        .more-docs {
          text-align: center;
          font-style: italic;
          font-size: 10px;
          margin-top: 4px;
          font-weight: bold;
        }
        
        .ticket-footer {
          text-align: center;
          margin-top: 8px;
          font-size: 10px;
          font-weight: bold;
        }
        
        .footer-text {
          font-weight: bold;
        }
        
        .print-time {
          font-size: 9px;
          color: #666;
          font-weight: bold;
        }
      `;
    },
  },
};
</script>

<style scoped>
.print-drawer {
  z-index: 1000;
}

.print-preview-container {
  height: calc(100vh - 200px);
  overflow-y: auto;
  background: #f5f5f5;
  padding: 16px;
}

.print-content {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
}

.pos-format {
  width: 80mm;
  font-family: "Courier New", monospace;
  font-size: 11px;
  line-height: 1.2;
  color: #000;
  padding: 8mm;
  margin: 0 auto;
  font-weight: bold; /* TODO EL TEXTO EN BOLD */
}

.ticket-header {
  text-align: center;
  margin-bottom: 12px;
}

.company-name {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 2px;
}

.company-nit {
  font-size: 10px;
  margin-bottom: 6px;
  color: #666;
  font-weight: bold;
}

.company-address,
.company-city,
.company-phone,
.company-email {
  font-weight: bold;
}

.ticket-title {
  font-weight: bold;
  font-size: 12px;
  margin-bottom: 6px;
}

.separator {
  text-align: center;
  margin: 6px 0;
  color: #666;
  font-family: monospace;
  font-weight: bold;
}

.info-line,
.summary-line {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2px;
  font-weight: bold;
}

.summary-line.total {
  font-weight: bold;
  font-size: 12px;
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  padding: 2px 0;
  margin-top: 4px;
}

.section-title {
  font-weight: bold;
  text-align: center;
  margin: 8px 0 6px 0;
  font-size: 11px;
}

.payment-line,
.doc-line,
.vendor-line {
  margin-bottom: 4px;
  font-weight: bold;
}

.payment-method,
.doc-type,
.vendor-name {
  font-weight: bold;
  margin-bottom: 1px;
}

.payment-details,
.doc-details,
.vendor-details {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #666;
  font-weight: bold;
}

.payment-type-line {
  margin-bottom: 4px;
  font-weight: bold;
}

.payment-type-name {
  font-weight: bold;
  margin-bottom: 1px;
}

.payment-type-details {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #666;
  font-weight: bold;
}

.payment-total {
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid #ddd;
}

.document-line {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  margin-bottom: 1px;
  padding: 1px 0;
  font-weight: bold;
}

.doc-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  font-weight: bold;
}

.doc-number {
  font-weight: bold;
}

.doc-type-small {
  font-size: 9px;
  color: #666;
  font-weight: bold;
}

.doc-value-small {
  font-weight: bold;
  text-align: right;
}

.more-docs {
  text-align: center;
  font-style: italic;
  font-size: 10px;
  margin-top: 6px;
  color: #666;
  font-weight: bold;
}

.ticket-footer {
  text-align: center;
  margin-top: 12px;
  font-size: 10px;
  font-weight: bold;
}

.footer-text {
  font-weight: bold;
  margin-bottom: 2px;
}

.print-time {
  font-size: 9px;
  color: #666;
  font-weight: bold;
}

.print-options {
  border-top: 1px solid #e0e0e0;
}
</style>
